import { ChangesDetection } from "@adas/ng-overlay";
import { I_UIConfigurationResponse, I_ObservationResponse } from "../api/observation.response";
import { C_PatientData } from "../../patient/domain/patient.model";
import { E_ObservationUnit } from "../../../../../enums/observation-unit.enum";
import { E_InsertMode } from "../../../../../enums/observation/observation-insert-mode.enum";
import { C_AlarmConfig } from "../../../../alarm/domain/alarm-config.model";

export class C_Observation extends ChangesDetection {
  // IDENTIFIERS
  localId: symbol = Symbol();
  id: string = '';
  patientId: string = '';
  userId?: string;
  systemId?: string;
  clinicalEpisode?: string;

  // OBSERVATION INFO
  code?: string;
  codingSystem?: string;
  name?: string;
  time?: Date;
  endTime?: Date;
  messageTime?: Date;
  value: any;
  units?: E_ObservationUnit | string;
  expires?: number;

  // CONFIG
  warnColor?: string;
  alertColor?: string;
  showOnExpired: boolean = false;
  insertMode: E_InsertMode = E_InsertMode.Auto;
  colorOnExpired?: string;
  status?: string; // StatusEnum.Type -> Default: Ok
  level?: number;
  uiConfiguration?: I_UIConfigurationResponse;
  alarm?: C_AlarmConfig;
  patient?: C_PatientData;

  // STATS RANGES
  max?: number;
  min?: number;
  maxWarn?: number;
  minWarn?: number;

  constructor(json?: I_ObservationResponse & { localId: symbol}) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  initializeFromJson(json: I_ObservationResponse & { localId: symbol}) {
    if (json.localId == this.localId) return;
    // IDENTIFIERS
    if (json.localId) this.localId = json.localId;
    if (json.id) this.id = json.id;
    if (json.patientId) this.patientId = json.patientId;
    if (json.userId) this.userId = json.userId;
    if (json.systemId) this.systemId = json.systemId;
    if (json.clinicalEpisode) this.clinicalEpisode = json.clinicalEpisode;

    // OBSERVATION INFO
    if (json.code) this.code = json.code;
    if (json.codingSystem) this.codingSystem = json.codingSystem;
    if (json.name) this.name = json.name;
    if (json?.time) this.time = new Date(json?.time);
    if (json?.endTime) this.endTime = new Date(json.endTime);
    if (json.messageTime) this.messageTime = new Date(json.messageTime);
    if (json?.value !== undefined && json?.value !== null) this.value = json.value;
    if (json.units) this.units = this.getUnits(json.units);
    if (json?.expires) this.expires = json.expires;

    // CONFIG
    if (json.warnColor) this.warnColor = json.warnColor;
    if (json.alertColor) this.alertColor = json.alertColor;
    this.showOnExpired = !!json.showOnExpired;
    if (json.insertMode) this.insertMode = json.insertMode;
    if (json.colorOnExpired) this.colorOnExpired = json.colorOnExpired;
    if (json.status) this.status = json.status;
    if (json?.level) this.level = json.level;
    if (json.uiConfiguration) this.uiConfiguration = json.uiConfiguration;
    if (json?.alarm) this.alarm = new C_AlarmConfig(json?.alarm);
    if (json.patient) this.patient = new C_PatientData(json.patient);

    // STATS RANGES
    if (json?.min) this.min = json.min;
    if (json?.max) this.max = json.max;
    if (json?.minWarn) this.minWarn = json.minWarn;
    if (json?.maxWarn) this.maxWarn = json.maxWarn;
  }

  public isExpired(): boolean {
    if (this && this.time != null && this.expires != null) {
      const now = Date.now();
      const expiredAt = new Date(this.time).getTime() + (this.expires * 1000);
      return now > expiredAt;
    }
    return false;
  }

  /**
 * Función que mapea el valor de la unidad a un string mas legible
 * @param unit
 * @private
 */

  private getUnits(unit: E_ObservationUnit) {
    switch (unit) {
      case E_ObservationUnit.MLPerKg:
        return $localize`:mL/kg|Unit of measurement for milliliters per kilogram@@observationSocketMl-Kg:mL/kg`;
      case E_ObservationUnit.MBar:
        return $localize`:mbar|Unit of measurement for millibar@@observationSocketMbar:mbar`;
      case E_ObservationUnit.ML:
        return $localize`:mL|Unit of measurement for milliliters@@observationSocketMl:mL`;
      case E_ObservationUnit.BeatPerMin:
      case E_ObservationUnit.RespPerMin:
        return $localize`:/min|Unit of measurement for beats or respirations per minute@@observationSocketMin:/min`;
      case E_ObservationUnit.LitresPerMin:
        return $localize`:L/min|Unit of measurement for liters per minute@@observationSocketL-Min:L/min`;
      case E_ObservationUnit.Percent:
        return $localize`:%%|Unit of measurement for percentage@@observationSocketPercentage:%`;
      case E_ObservationUnit.Mmhg:
        return $localize`:mm[Hg]|Unit of measurement for millimeters of mercury@@observationSocketMmHg:mm[Hg]`;
      case E_ObservationUnit.Cm_H2O:
        return $localize`:cm[H2O]|Unit of measurement for centimeters of water@@observationSocketCmH2o:cm[H2O]`;
      case E_ObservationUnit.DimLess:
        return $localize`:DimLess|Unit of measurement for dimensionless quantities@@observationSocketDimLess:DimLess`;
      default:
        return unit;
    }
  }

  public clone(): C_Observation {
    const clone = new C_Observation();
    clone.value = Array.isArray(this.value) ? [...this.value] : (typeof this.value === 'object' && this.value !== null ? { ...this.value } : this.value);
    clone.level = this.level;
    clone.endTime = this.endTime;
    clone.name = this.name;
    clone.units = this.units;
    clone.min = this.min;
    clone.minWarn = this.minWarn;
    clone.max = this.max;
    clone.maxWarn = this.maxWarn;
    clone.status = this.status;
    clone.expires = this.expires;
    clone.showOnExpired = this.showOnExpired;
    if (this.time) {
      clone.time = new Date(this.time.getTime());
    }
    if (this.alarm) {
      clone.alarm = Object.assign(new C_AlarmConfig(), this.alarm);
    }

    return clone;
  }

}
